// src/oura/delivery/useDelivery.ts
//
// One client's Delivery, loaded once and read again after any write (the
// service broadcasts), so the list, the drawer and the header never disagree.

import { useCallback, useEffect, useRef, useState } from "react";
import { getDelivery, subscribeDelivery, type Deliverable, type DeliveryFailure, type DeliveryPayload } from "@service/aeo/delivery";

export type DeliveryLoad =
    | { state: "loading" }
    | { state: "failed"; failure: DeliveryFailure }
    | { state: "none" }
    /** `stale`: a background read failed, so this is the last good read. */
    | { state: "ready"; data: DeliveryPayload; stale?: boolean };

/** How often a running check is read again. A 600-page site takes about a minute. */
const CHECK_POLL_MS = 5_000;
const FOCUS_READ_GAP_MS = 15_000;

export function useDelivery(businessId: string, businessName?: string) {
    const [load, setLoad] = useState<DeliveryLoad>({ state: "loading" });
    const seq = useRef(0);

    // Settles when this read has landed or been superseded, so a poll can wait for it.
    const reload = useCallback(
        (quiet = true): Promise<void> => {
            const mine = ++seq.current;
            if (!quiet) setLoad({ state: "loading" });
            return getDelivery(businessId, businessName).then((r) => {
                if (mine !== seq.current) return;
                // A failed background read keeps the last good data (an open drawer or a half-typed form survives).
                if (!r.ok) setLoad((l) => (quiet && l.state === "ready" ? { ...l, stale: true } : { state: "failed", failure: r }));
                else if (!r.data) setLoad({ state: "none" });
                else setLoad({ state: "ready", data: r.data });
            });
        },
        [businessId, businessName],
    );

    useEffect(() => {
        void reload(false);
        return subscribeDelivery(() => void reload(true));
    }, [reload]);

    // A live check finishes on the server, which cannot tell this browser: read
    // again while one runs, until it stops or the screen goes. One poll at a
    // time: the next is scheduled only after the last read settles, so on a
    // slow connection polls never supersede each other and a finished check
    // always lands.
    const running = load.state === "ready" && load.data.lastCheck.running;
    useEffect(() => {
        if (!running) return;
        let stopped = false;
        let timer: number | undefined;
        const poll = () => {
            if (stopped) return;
            void reload(true).finally(() => {
                if (!stopped) timer = window.setTimeout(poll, CHECK_POLL_MS);
            });
        };
        timer = window.setTimeout(poll, CHECK_POLL_MS);
        return () => {
            stopped = true;
            window.clearTimeout(timer);
        };
    }, [running, reload]);

    // Coming back to the tab or the window reads again, at most every 15 seconds.
    const lastFocusRead = useRef(0);
    useEffect(() => {
        const onBack = () => {
            if (document.visibilityState !== "visible" || Date.now() - lastFocusRead.current < FOCUS_READ_GAP_MS) return;
            lastFocusRead.current = Date.now();
            void reload(true);
        };
        document.addEventListener("visibilitychange", onBack);
        window.addEventListener("focus", onBack);
        return () => {
            document.removeEventListener("visibilitychange", onBack);
            window.removeEventListener("focus", onBack);
        };
    }, [reload]);

    /** Put one deliverable a write returned in place, before the reload lands. */
    const apply = useCallback((d: Deliverable) => {
        setLoad((l) => (l.state === "ready" ? { state: "ready", data: { ...l.data, deliverables: l.data.deliverables.map((x) => (x.key === d.key ? d : x)) } } : l));
    }, []);

    return { load, reload, apply };
}
